import type { NavigateFunction } from 'react-router';

import { UPDATED_PRODUCT_KEYS } from '@cartwhl/common/stores/updatedProductStore';
import {
  OrganizationInfo,
  StripeCustomer,
  StripePlan,
  StripePrice,
  UserInfo,
} from '@cartwhl/common/types';
import { getStripePortalSession } from '@cartwhl/common/utils/getStripePortalSession';
import { getStripeProductID } from '@cartwhl/common/utils/getStripeProductID';

import { checkoutStore } from '../../../stores/global';

export type SubscriberPremiumStatus = 'isPaying' | 'willExpire' | 'expired';

export interface HandlePricingPlanSelectionArgs {
  authStatus: string;
  hasExistingSubscription: boolean;
  currentProductId: string | null;
  productId: string;
  stripeCustomerId: string;
  price: StripePrice;
  navigate: NavigateFunction;
}

function isUpdatedProductPlanType(
  planType: string | undefined,
): planType is (typeof UPDATED_PRODUCT_KEYS)[number] {
  return (
    planType != null &&
    (UPDATED_PRODUCT_KEYS as readonly string[]).includes(planType)
  );
}

function productIdFromPlanType(planType: string | undefined): string | null {
  if (!planType) {
    return null;
  }

  if (isUpdatedProductPlanType(planType)) {
    try {
      return getStripeProductID(planType);
    } catch {
      return null;
    }
  }

  if (planType === 'legacy_pro') {
    return getStripeProductID('pro');
  }

  if (planType === 'legacy_unlimited') {
    return getStripeProductID('advanced');
  }

  return null;
}

/** True when the user already has a subscription (paid, trialing, or legacy) and should not start a new trial. */
export function hasExistingSubscriptionForPricing(
  subscriberPremiumStatus: SubscriberPremiumStatus,
  inStripeFreeTrialPeriod: boolean | undefined,
): boolean {
  return (
    subscriberPremiumStatus === 'isPaying' ||
    subscriberPremiumStatus === 'willExpire' ||
    inStripeFreeTrialPeriod === true
  );
}

export function getCurrentSubscriptionProductId(
  customer: StripeCustomer | null | undefined,
  organizationInfo?: OrganizationInfo | null,
  userInfo?: Pick<UserInfo, 'planType'> | null,
): string | null {
  const plan = customer?.plans?.find((item: StripePlan) => item.active);
  if (plan?.product) {
    return plan.product;
  }

  const fromOrgPlan = productIdFromPlanType(organizationInfo?.planType);
  if (fromOrgPlan) {
    return fromOrgPlan;
  }

  if (userInfo?.planType === 'pro') {
    return getStripeProductID('pro');
  }

  if (userInfo?.planType === 'unlimited') {
    return getStripeProductID('advanced');
  }

  return null;
}

export function getPricingPlanCTA(
  authStatus: string,
  hasExistingSubscription: boolean,
  currentProductId: string | null,
  cardProductId: string | undefined,
  productName: string,
) {
  if (!cardProductId) {
    return 'Loading...';
  }

  if (hasExistingSubscription && currentProductId === cardProductId) {
    return 'Your current plan';
  }

  if (hasExistingSubscription) {
    return `Change plan - ${productName}`;
  }

  if (authStatus === 'anonymous') {
    return 'Start 7-day free trial';
  }

  return 'Start 7-day free trial';
}

export async function handlePricingPlanSelection({
  authStatus,
  hasExistingSubscription,
  currentProductId,
  productId,
  stripeCustomerId,
  price,
  navigate,
}: HandlePricingPlanSelectionArgs) {
  if (authStatus === 'anonymous') {
    checkoutStore.select(price);
    const checkoutParams = new URLSearchParams({
      priceID: price.id,
    });
    navigate(`/sign-up?redirectTo=/checkout?${checkoutParams.toString()}`);
    return;
  }

  if (hasExistingSubscription) {
    if (currentProductId === productId) {
      return;
    }

    if (!stripeCustomerId) {
      return;
    }

    const portalSession = await getStripePortalSession(
      stripeCustomerId,
      price.id,
    );
    if (portalSession?.data) {
      window.location.href = portalSession.data.url;
    }
    return;
  }

  checkoutStore.select(price);
  await checkoutStore.startCheckout();
}
